import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, fileUrl, thumbUrl, videoUrl, posterUrl, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Swords, Loader2, Trophy, Zap, Star } from "lucide-react";
import { toast } from "sonner";

const Battles = () => {
  const { requireAction, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("play");
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [tally, setTally] = useState(0);
  const [rankings, setRankings] = useState([]);
  const [rankLoading, setRankLoading] = useState(false);

  const loadPair = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/battle/pair"); setPair(r.data); } catch { setPair(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPair(); }, [loadPair]);

  useEffect(() => {
    if (tab === "play") return;
    setRankLoading(true);
    api.get("/battle/rankings", { params: { period: tab } }).then((r) => setRankings(r.data)).catch(() => setRankings([])).finally(() => setRankLoading(false));
  }, [tab]);

  const pick = async (winner, loser) => {
    if (!requireAction()) return;
    if (voting) return;
    setVoting(true);
    try {
      await api.post("/battle/vote", { winner_id: winner.id, loser_id: loser.id });
      setTally((t) => t + 1);
      await loadPair();
    } catch (e) { toast.error(e?.response?.data?.detail || "Vote failed"); }
    finally { setVoting(false); }
  };

  const Card = ({ item, other }) => (
    <button data-testid={`battle-pick-${item.id}`} onClick={() => pick(item, other)} disabled={voting}
      className="group relative flex-1 rounded-2xl overflow-hidden border border-border hover:border-primary transition-all disabled:opacity-60">
      <div className="aspect-[3/4] sm:aspect-square lg:aspect-[4/5] bg-black">
        {item.media_type === "video"
          ? <video src={videoUrl(item)} poster={posterUrl(item, 1200)} muted loop autoPlay playsInline className="w-full h-full object-cover" />
          : <img src={thumbUrl(item.file_ref, 1200)} alt={item.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
        <div className="font-heading text-lg truncate">{item.title}</div>
        {item.location?.display && <div className="text-xs text-muted-foreground">{item.location.display}</div>}
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-bold text-lg flex items-center gap-2"><Zap className="w-5 h-5 fill-current" /> Pick</span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center gap-2 text-primary mb-1"><Swords className="w-4 h-4" /><span className="text-xs tracking-[0.2em] uppercase font-bold">Head-to-Head</span></div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter">Which one wins?</h1>
            <p className="text-muted-foreground mt-2">One tap crowns a winner. {tally > 0 && <span className="text-primary font-medium">{tally} battles judged</span>}</p>
          </div>
          <div className="flex bg-card border border-border rounded-full p-1">
            {[["play", "Battle"], ["daily", "Daily"], ["weekly", "Weekly"], ["all", "All-Time"]].map(([k, l]) => (
              <button key={k} data-testid={`battle-tab-${k}`} onClick={() => setTab(k)}
                className={cn("px-4 py-1.5 rounded-full text-sm transition-colors", tab === k ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
          </div>
        </div>

        {tab === "play" ? (
          loading ? <div className="h-[420px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            : !pair ? <div className="h-[420px] flex items-center justify-center text-muted-foreground">Not enough content for a battle yet.</div>
            : (
            <div className="mt-8 flex items-stretch gap-3 sm:gap-6 relative">
              <Card item={pair.a} other={pair.b} />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-background border-2 border-primary flex items-center justify-center font-heading text-primary text-lg shadow-xl">VS</div>
              <Card item={pair.b} other={pair.a} />
            </div>
          )
        ) : (
          <div className="mt-8">
            {rankLoading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
              : rankings.length === 0 ? <div className="border border-dashed border-border rounded-xl py-16 text-center text-muted-foreground">No battles in this period yet — go crown some winners!</div>
              : <div className="space-y-2">
                {rankings.map((m, i) => (
                  <div key={m.id} data-testid={`battle-rank-${m.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <span className={cn("font-heading text-xl w-8 text-center", i === 0 ? "text-primary" : "text-muted-foreground")}>{i + 1}</span>
                    <button onClick={() => navigate(`/place/${m.location?.slug || ""}`)} className="w-16 h-12 rounded-lg overflow-hidden bg-black shrink-0">
                      {m.media_type === "video" ? <video src={videoUrl(m)} poster={posterUrl(m, 300)} muted className="w-full h-full object-cover" /> : <img src={thumbUrl(m.file_ref, 300)} className="w-full h-full object-cover" alt="" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{m.title}</div>
                      <div className="text-xs text-muted-foreground truncate">@{m.uploader_name}{m.location?.display ? ` · ${m.location.display}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1.5 text-primary font-bold"><Trophy className="w-4 h-4" />{m.wins}<span className="text-muted-foreground text-xs font-normal">wins</span></div>
                    {m.win_rate !== undefined && <div className="text-xs text-muted-foreground w-16 text-right hidden sm:block">{m.win_rate}% rate</div>}
                  </div>
                ))}
              </div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Battles;
